/* Görünüm — sitenin sayfalarını Word'deki gibi yerinde düzenleme.

   Kabuk editor/shell.ts'te; burası yalnızca neyin düzenlendiğini tarif
   ediyor: tasarım belgesi (packages/shared/design.ts) ve onun bölgeleri.

     Ana sayfa      `home`
     Bütün işler    `pages.works`
     Günlük         `pages.blog` (liste sayfasının başlığı; yazılar Günlük
                    sekmesinde)
     404            `pages.notFound`

   Menü ve footer (`shared`) her bölgenin önizlemesinde görünüyor ve orada
   düzenleniyor. Editörün kök listesi bu yüzden `[menü, footer, …bölge]`;
   değişiklikten sonra liste yeniden ikiye ayrılıyor.

   Sekmeden çıkıp dönmek kayıtsız değişiklikleri silmiyor (durum modül
   düzeyinde). */

import { designSchema, type Block, type Design, type Tokens } from '@frawgraphy/shared';
import type { SiteContent } from '@frawgraphy/tools/content';
import { defaultHome } from '@frawgraphy/tools/blocks/home';
import { defaultPage, defaultShared, type PageName } from '@frawgraphy/tools/blocks/defaults';
import type { PageKey } from '@frawgraphy/tools/pages';
import { design } from '../api.ts';
import { el, replace } from '../dom.ts';
import type { AppContext } from '../main.ts';
import { renderInspector } from '../editor/inspector.ts';
import { previewPage } from '../editor/page.ts';
import { mountEditor, type EditorSource } from '../editor/shell.ts';

type Region = 'home' | PageName;

type Doc = { tokens: Tokens; home: Block[]; shared: Block[]; pages: Record<PageName, Block[]> };

type State = { doc: Doc; saved: string; live: string; content: SiteContent; region: Region };

let state: State | null = null;

const REGIONS: { key: Region; label: string; page: PageKey }[] = [
  { key: 'home', label: 'Ana sayfa', page: 'index' },
  { key: 'works', label: 'Bütün işler', page: 'works' },
  { key: 'blog', label: 'Günlük (liste sayfası)', page: 'blog' },
  { key: 'notFound', label: '404 sayfası', page: '404' }
];

function complete(d: Design | null, content: SiteContent): Doc {
  return {
    tokens: d?.tokens ?? { dark: {}, light: {} },
    home: d?.home ?? defaultHome(content),
    shared: d?.shared ?? defaultShared(),
    pages: {
      works: d?.pages?.works ?? defaultPage('works'),
      blog: d?.pages?.blog ?? defaultPage('blog'),
      notFound: d?.pages?.notFound ?? defaultPage('notFound')
    }
  };
}

const toDesign = (d: Doc): Design => ({ version: 1, tokens: d.tokens, home: d.home, shared: d.shared, pages: d.pages });

export async function renderDesign(host: HTMLElement, ctx: AppContext): Promise<void> {
  replace(host, el('h1', { text: 'Görünüm' }), el('p', { class: 'note', text: 'Tasarım ve içerik okunuyor…' }));
  if (!state) {
    try {
      const [current, content] = await Promise.all([design.read(), design.content()]);
      const doc = complete(current.draft.doc, content);
      state = {
        doc,
        /* Kayıtlı taslakta olmayan parçalar varsayılanla dolduruldu; bunlar
           "kaydedilmiş" sayılıyor — hiçbir şey değiştirmeden kirli görünmesin. */
        saved: JSON.stringify(doc),
        live: JSON.stringify(complete(current.live?.doc ?? null, content)),
        content,
        region: 'home'
      };
    } catch (err) {
      ctx.fail(err);
      replace(host, el('h1', { text: 'Görünüm' }), el('p', { class: 'err', text: err instanceof Error ? err.message : 'Okunamadı.' }));
      return;
    }
  }
  const s = state;

  const regionList = () => (s.region === 'home' ? s.doc.home : s.doc.pages[s.region]);

  const source: EditorSource = {
    title: 'Görünüm',
    regions: REGIONS.map((r) => ({ key: r.key, label: r.label })),
    region: () => s.region,
    setRegion: (key) => {
      s.region = key as Region;
    },
    root: () => [...s.doc.shared, ...regionList()],
    apply: (next) => {
      const shared = next.filter((b) => b.type === 'nav' || b.type === 'footer');
      const rest = next.filter((b) => b.type !== 'nav' && b.type !== 'footer');
      const candidate: Doc = structuredClone(s.doc);
      candidate.shared = shared;
      if (s.region === 'home') candidate.home = rest;
      else candidate.pages[s.region] = rest;
      const parsed = designSchema.safeParse(toDesign(candidate));
      if (!parsed.success) return parsed.error.issues[0]?.message ?? 'Geçersiz değişiklik.';
      s.doc = complete(parsed.data, s.content);
      return null;
    },
    preview: () => {
      const page = REGIONS.find((r) => r.key === s.region)!.page;
      return previewPage(toDesign(s.doc), s.content, page);
    },
    snapshot: () => JSON.stringify(s.doc),
    restore: (snap) => {
      s.doc = JSON.parse(snap) as Doc;
    },
    savedSnapshot: () => s.saved,
    liveSnapshot: () => s.live,
    save: async () => {
      await design.save(toDesign(s.doc));
      s.saved = JSON.stringify(s.doc);
    },
    promote: async () => {
      await design.promote();
      s.live = JSON.stringify(s.doc);
    },
    versions: async () => (await design.versions()).versions,
    loadVersion: async (id) => {
      const { doc } = await design.version(id);
      s.doc = complete(doc, s.content);
    },
    sidePanel: (panel, ictx) => renderInspector(panel, null, ictx),
    inspectorExtras: {
      theme: () => 'dark',
      tokens: () => s.doc.tokens,
      setTokens: (fn) => {
        const next = structuredClone(s.doc.tokens);
        fn(next);
        const parsed = designSchema.safeParse({ ...toDesign(s.doc), tokens: next });
        if (parsed.success) s.doc.tokens = parsed.data.tokens;
        else ctx.toast(parsed.error.issues[0]?.message ?? 'Geçersiz renk.', 'bad');
      }
    }
  };

  mountEditor(host, ctx, source);
}
